import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { PlayerBike } from '../entities/PlayerBike';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraRig } from '../systems/CameraRig';
import { CellSystem } from '../systems/CellSystem';
import { EnvironmentSystem } from '../systems/EnvironmentSystem';
import { Hud } from '../systems/Hud';
import { ObstacleSystem } from '../systems/ObstacleSystem';
import { PatternDirector } from '../systems/PatternDirector';
import { RenderPipeline } from '../systems/RenderPipeline';
import { TrackSystem } from '../systems/TrackSystem';
import { VfxSystem } from '../systems/VfxSystem';
import { DebugTools } from '../systems/DebugTools';
import { ShakeRig } from '../systems/effects/ShakeRig';
import { TweenManager, easeOutCubic } from '../systems/effects/Tween';
import { createSeededRandom } from '../utils/random';
import { PALETTE } from '../assets/palette';
import { DEFAULT_TUNING, RUN, SPEED, type GameState } from './tuning';

const BEST_KEY = 'neon-drift-best';
const TITLE_CRUISE_SPEED = 9;

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly input: InputController;
  private readonly tweens = new TweenManager();
  private readonly player = new PlayerBike(this.tweens);
  private readonly track = new TrackSystem();
  private readonly obstacles = new ObstacleSystem();
  private readonly cells = new CellSystem();
  private readonly environment = new EnvironmentSystem();
  private readonly vfx = new VfxSystem();
  private readonly pipeline: RenderPipeline;
  private readonly audio = new AudioSystem();
  private readonly shake = new ShakeRig();
  private readonly hud: Hud;
  private readonly cameraRig: CameraRig;
  private readonly director: PatternDirector;
  private readonly debugTools: DebugTools;
  private readonly loop = new Loop(
    (delta, elapsed) => this.update(delta, elapsed),
    () => this.render(),
  );

  private readonly tuning = { ...DEFAULT_TUNING };

  private state: GameState = 'title';
  private frame = 0;
  private elapsed = 0;
  private runTime = 0;
  private distance = 0;
  private score = 0;
  private best = 0;
  private cellsCollected = 0;
  private boostPool = 0;
  private overdriveMeter = 0;
  private overdriveTimer = 0;
  private timeScale = 1;
  private hitstopRemaining = 0;
  private crashTimer = 0;
  private stateGuardFrame = -1;
  private rng = createSeededRandom(1);
  private pausedForScreenshot = false;
  private reducedMotion = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.camera = new THREE.PerspectiveCamera(this.tuning.fov, 1, 0.1, 260);
    this.cameraRig = new CameraRig(this.camera);
    this.renderer.toneMappingExposure = this.tuning.exposure;
    this.pipeline = new RenderPipeline(this.renderer, this.scene, this.camera, this.tuning.bloom);
    this.input = new InputController(document.body);
    this.director = new PatternDirector(this.obstacles, this.cells, this.rng);

    this.hud = new Hud({
      onStart: () => this.startRun(),
      onResume: () => this.resume(),
      onRestart: () => this.startRun(),
      onPause: () => this.pause(),
      onToggleMute: () => {
        this.audio.setMuted(!this.audio.isMuted());
        return this.audio.isMuted();
      },
    });

    this.debugTools = new DebugTools(this.tuning, () => {
      this.renderer.toneMappingExposure = this.tuning.exposure;
      this.pipeline.setBloom(this.tuning.bloom);
      this.cameraRig.setBaseFov(this.tuning.fov);
      this.syncSize(resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr));
    });

    this.best = this.loadBest();
    this.createScene();
    // Precompile every scene material now so first-gameplay frames don't
    // stall on shader compilation (which also delays input event handling).
    this.renderer.compile(this.scene, this.camera);
    this.setState('title');
    this.cameraRig.snapTo(0);
    this.syncSize(resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr));
    this.installTestHooks();
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.audio.dispose();
    this.debugTools.dispose();
    this.player.dispose();
    this.track.dispose();
    this.obstacles.dispose();
    this.cells.dispose();
    this.environment.dispose();
    this.vfx.dispose();
    this.pipeline.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  // ---------- State transitions ----------

  private setState(next: GameState): void {
    this.state = next;
    this.stateGuardFrame = this.frame;
    this.hud.setState(next);
  }

  private startRun(): void {
    this.resetRun();
    this.setState('running');
    this.audio.uiClick();
    this.audio.startEngine();
  }

  private pause(): void {
    if (this.state !== 'running') return;
    this.setState('paused');
    this.audio.uiBack();
    this.audio.setDuck(0.3);
  }

  private resume(): void {
    if (this.state !== 'paused') return;
    this.setState('running');
    this.audio.uiClick();
    this.audio.setDuck(1);
  }

  private resetRun(): void {
    this.runTime = 0;
    this.distance = 0;
    this.score = 0;
    this.cellsCollected = 0;
    this.boostPool = 0;
    this.overdriveMeter = 0;
    this.overdriveTimer = 0;
    this.timeScale = 1;
    this.hitstopRemaining = 0;
    this.crashTimer = 0;
    this.tweens.clear();
    this.shake.reset();
    this.player.reset();
    this.obstacles.reset();
    this.cells.reset();
    this.vfx.reset();
    this.director.reset();
    this.audio.setDuck(1);
    this.hud.setMeterCrashed(false);
    this.cameraRig.snapTo(0);
  }

  private crash(): void {
    if (this.player.crashed) return;
    this.player.crash();
    this.crashTimer = RUN.crashSlowmoDuration;
    this.hitstop(90, 0.05);
    this.shake.addTrauma(0.7);
    this.hud.flash();
    this.hud.setMeterCrashed(true);
    this.audio.crash();
    this.audio.setDuck(0.4);
    this.cameraRig.punchFov(7);
    this.vfx.burst(this.player.visualX, 0.6, 0, '#ffe9c4', 1.6, 0.8);
    this.vfx.sparkBurst(this.player.visualX, 0.7, 0, '#ffd2a8', 12, 8, 1.2);

    // Tumble the bike with real-delta tweens so it stays live in slow-mo.
    const root = this.player.group;
    const startRotY = root.rotation.y;
    this.tweens.tween(
      1.0,
      (t) => {
        root.rotation.y = startRotY + t * 2.6;
        root.rotation.z = Math.sin(t * Math.PI) * 0.7;
        root.position.y = Math.sin(t * Math.PI) * 0.9;
      },
      easeOutCubic,
    );
  }

  private finishCrash(): void {
    const isRecord = this.score > this.best && this.best > 0;
    if (this.score > this.best) {
      this.best = this.score;
      this.saveBest();
    }
    this.audio.stopEngine();
    this.audio.setDuck(1);
    this.hud.showGameOver(this.score, this.distance, this.best, isRecord);
    this.setState('gameover');
  }

  private hitstop(durationMs: number, scale = 0.05): void {
    this.hitstopRemaining = Math.max(this.hitstopRemaining, durationMs / 1000);
    this.timeScale = scale;
  }

  // ---------- Frame update ----------

  private update(delta: number, elapsed: number): void {
    this.frame += 1;
    this.elapsed = elapsed;
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }

    this.syncSize(resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr));
    this.handleIntents();

    // Hitstop decays in REAL time; crash slow-mo follows it.
    if (this.hitstopRemaining > 0) {
      this.hitstopRemaining -= delta;
      if (this.hitstopRemaining <= 0) {
        this.timeScale = this.crashTimer > 0 ? RUN.crashSlowmoScale : 1;
      }
    }
    const gameplayDelta = delta * this.timeScale;
    const animElapsed = this.reducedMotion ? 0 : elapsed;

    if (this.state === 'running') {
      this.updateRunning(gameplayDelta, animElapsed, delta);
    } else if (this.state === 'title') {
      const slide = this.reducedMotion ? 0 : TITLE_CRUISE_SPEED * gameplayDelta;
      this.track.update(slide);
      this.environment.update(slide);
      this.player.update(gameplayDelta, animElapsed, 0.25);
    }
    // Paused/gameover: world frozen; UI screens own the moment.

    // Feedback layers always read the REAL delta.
    this.tweens.update(delta);
    this.vfx.update(delta);
    const speedRatio = this.currentSpeed() / (SPEED.maxBase + SPEED.maxBoost);
    this.cameraRig.update(delta, this.player.visualX, this.tuning.cameraLag, this.state === 'running' ? speedRatio : 0.1);
    this.shake.update(delta, this.camera);
    this.hud.update(
      this.score,
      this.best,
      this.currentSpeed() * 3.6,
      this.distance,
      this.overdriveTimer > 0 ? this.overdriveTimer / RUN.overdriveDuration : this.overdriveMeter,
      this.overdriveTimer > 0,
    );
    this.publishDiagnostics();
  }

  private updateRunning(gameplayDelta: number, animElapsed: number, realDelta: number): void {
    if (!this.player.crashed) {
      this.runTime += gameplayDelta;

      // Speed model: ramping base + decaying cell boost, overdrive multiplier.
      this.boostPool = Math.max(0, this.boostPool - SPEED.boostDecay * gameplayDelta);
      if (this.overdriveTimer > 0) {
        this.overdriveTimer -= gameplayDelta;
        if (this.overdriveTimer <= 0) {
          this.overdriveMeter = 0;
          this.hud.showPopup('OVERDRIVE AGOTADO', 'alert');
        }
      }
    }

    const speed = this.player.crashed ? 0 : this.currentSpeed();
    const distanceDelta = speed * gameplayDelta;
    this.distance += distanceDelta;
    const multiplier = this.overdriveTimer > 0 ? 2 : 1;
    this.score += distanceDelta * RUN.distanceScore * multiplier;

    this.track.update(distanceDelta);
    this.environment.update(distanceDelta);
    this.director.update(distanceDelta, this.distance);
    this.obstacles.update(distanceDelta, animElapsed);

    const speedRatio = speed / (SPEED.maxBase + SPEED.maxBoost);
    const magnetRadius = 1.15 + speedRatio * 0.6;
    const { collected, superCollected } = this.cells.update(
      distanceDelta,
      gameplayDelta,
      animElapsed,
      this.player.visualX,
      magnetRadius,
      !this.player.crashed,
    );
    if (collected > 0 || superCollected > 0) {
      this.onCellsCollected(collected, superCollected, multiplier);
    }

    if (!this.player.crashed) {
      const hit = this.obstacles.collide(
        this.player.visualX,
        PlayerBike.HALF_WIDTH,
        PlayerBike.HALF_LENGTH,
      );
      if (hit) {
        this.crash();
      } else {
        const nearMisses = this.obstacles.countNearMisses(this.player.visualX, RUN.nearMissWindow);
        if (nearMisses > 0) {
          this.score += nearMisses * RUN.nearMissBonus * multiplier;
          this.hud.showPopup(`NEAR MISS +${nearMisses * RUN.nearMissBonus * multiplier}`);
          this.hud.punchScore();
          this.audio.nearMiss();
          this.shake.addTrauma(0.12);
          const side = this.rng() < 0.5 ? -1 : 1;
          this.vfx.sparkBurst(this.player.visualX + side * 0.9, 0.6, 0.4, PALETTE.playerAccent, 4, 5);
        }
      }
    }

    this.player.update(gameplayDelta, animElapsed, speedRatio);
    this.audio.updateEngine(speedRatio, this.overdriveTimer > 0);

    if (this.crashTimer > 0) {
      this.crashTimer -= realDelta;
      if (this.crashTimer <= 0) {
        this.timeScale = 1;
        this.finishCrash();
      }
    }
  }

  private onCellsCollected(collected: number, superCollected: number, multiplier: number): void {
    const total = collected + superCollected;
    this.cellsCollected += total;
    this.score += (collected * RUN.cellScore + superCollected * RUN.cellScore * 4) * multiplier;
    this.boostPool = Math.min(SPEED.maxBoost, this.boostPool + total * SPEED.cellBoost);
    this.audio.cell(this.rng, superCollected > 0);
    this.hud.punchScore();
    this.cameraRig.punchFov(1.6 + superCollected * 2);
    this.vfx.burst(
      this.player.visualX,
      0.85,
      0,
      superCollected > 0 ? PALETTE.supercell : PALETTE.cell,
      superCollected > 0 ? 1.4 : 0.9,
    );
    if (superCollected > 0) this.hud.showPopup('SUPERCELL +' + superCollected * RUN.cellScore * 4 * multiplier);

    if (this.overdriveTimer <= 0) {
      this.overdriveMeter = Math.min(
        1,
        this.overdriveMeter + total * RUN.overdrivePerCell + superCollected * 0.2,
      );
      if (this.overdriveMeter >= 1) {
        this.overdriveTimer = RUN.overdriveDuration;
        this.hud.showPopup('OVERDRIVE ×2');
        this.audio.overdrive();
        this.cameraRig.punchFov(6);
        this.shake.addTrauma(0.18);
        this.vfx.burst(this.player.visualX, 0.5, 0, PALETTE.playerAccent, 2.2, 0.6);
      }
    }
  }

  private currentSpeed(): number {
    const base = Math.min(SPEED.base + this.runTime * SPEED.ramp, SPEED.maxBase);
    const overdrive = this.overdriveTimer > 0 ? SPEED.overdriveMultiplier : 1;
    return (base + this.boostPool) * overdrive;
  }

  private handleIntents(): void {
    const freshState = this.stateGuardFrame === this.frame;
    this.input.consume((intent) => {
      if (freshState) return; // ignore inputs queued before a state switch
      switch (this.state) {
        case 'title':
          if (intent === 'confirm') this.startRun();
          break;
        case 'running':
          if (intent === 'laneLeft') this.onLaneChange(-1);
          else if (intent === 'laneRight') this.onLaneChange(1);
          else if (intent === 'pause') this.pause();
          break;
        case 'paused':
          if (intent === 'pause' || intent === 'confirm') this.resume();
          break;
        case 'gameover':
          if (intent === 'confirm') this.startRun();
          break;
      }
    });
  }

  private onLaneChange(direction: -1 | 1): void {
    if (this.player.crashed) return;
    if (this.player.tryChangeLane(direction)) {
      this.shake.addTrauma(0.05);
    }
  }

  private render(): void {
    this.pipeline.render();
  }

  /** Keep the composer sized with the canvas after any renderer resize. */
  private syncSize(resized: boolean): void {
    if (!resized) return;
    const dpr = Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr);
    this.pipeline.setSize(this.canvas.clientWidth, this.canvas.clientHeight, dpr);
  }

  // ---------- Scene ----------

  private createScene(): void {
    this.scene.background = new THREE.Color(PALETTE.void);
    // Light haze: reveals depth without swallowing the skyline layers.
    this.scene.fog = new THREE.FogExp2(PALETTE.fogColor, 0.0095);

    // Neutral IBL so the metallic deck and hull read instead of going flat.
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.35;
    pmrem.dispose();

    const hemisphere = new THREE.HemisphereLight('#2a3d66', '#05050c', 1.2);
    this.scene.add(hemisphere);

    const key = new THREE.DirectionalLight('#7fd6ff', 1.4);
    key.position.set(-6, 12, 4);
    this.scene.add(key);

    // Magenta rim from ahead-left: separates hull and hazards from the void.
    const rim = new THREE.DirectionalLight('#ff4db8', 0.85);
    rim.position.set(-4, 5, -14);
    this.scene.add(rim);

    this.scene.add(this.environment.group);
    this.scene.add(this.track.group);
    this.scene.add(this.obstacles.group);
    this.scene.add(this.cells.group);
    this.scene.add(this.vfx.group);
    this.scene.add(this.player.group);
  }

  // ---------- Persistence ----------

  private loadBest(): number {
    try {
      return Number(localStorage.getItem(BEST_KEY)) || 0;
    } catch {
      return 0;
    }
  }

  private saveBest(): void {
    try {
      localStorage.setItem(BEST_KEY, String(Math.floor(this.best)));
    } catch {
      // Storage unavailable (private mode); best resets per session.
    }
  }

  // ---------- Test hooks & diagnostics ----------

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
        this.director.setRng(this.rng);
      },
      setState: (name: string) => {
        if (name === 'active-play') {
          this.startRun();
        } else if (name === 'title') {
          this.resetRun();
          this.setState('title');
        } else if (name === 'fail' || name === 'gameover' || name === 'complete') {
          this.startRun();
          this.crash();
          this.crashTimer = 0.001;
        } else if (name === 'crash') {
          // Held slow-mo wreck: lets capture tools frame sparks and tumble.
          this.startRun();
          this.crash();
          this.crashTimer = 30;
        } else if (name === 'stress') {
          this.startRun();
          // Dense worst-case field for perf measurement.
          const lanes: Array<-1 | 0 | 1> = [-1, 0, 1];
          for (let i = 0; i < 12; i += 1) {
            const z = -20 - i * 10;
            this.obstacles.spawn('firewall', lanes[i % 3], z);
            this.obstacles.spawn('pylon', lanes[(i + 1) % 3], z - 4);
            this.obstacles.spawn('drone', lanes[(i + 2) % 3], z - 7, {
              toLane: lanes[i % 3],
              phase: i,
              speed: 2,
            });
            this.cells.spawn(lanes[(i + 2) % 3], z - 2);
            this.cells.spawn(lanes[(i + 1) % 3], z - 5, i % 4 === 0);
          }
        } else {
          console.warn(`Unknown test state: ${name}`);
        }
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
        this.shake.setIntensity(enabled ? 0 : 1);
      },
      hideDebugUi: (hidden: boolean) => {
        this.debugTools.setHidden(hidden);
      },
    };
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      score: Math.floor(this.score),
      targetScore: Math.max(Math.floor(this.best), 1),
      complete: this.state === 'gameover',
      state: this.state,
      distance: Math.floor(this.distance),
      cellsCollected: this.cellsCollected,
      obstaclesActive: this.obstacles.activeCount(),
      cellsActive: this.cells.activeCount(),
      player: {
        lane: this.player.lane,
        rotationY: this.player.group.rotation.y,
        // Virtual world position: x is the lane offset, z decreases with travel
        // so tests can assert forward progress.
        position: {
          x: this.player.visualX,
          y: this.player.group.position.y,
          z: -this.distance,
        },
        speed: this.state === 'running' && !this.player.crashed ? this.currentSpeed() : 0,
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }
}
